'use strict';

import {
  RoomId,
  Entity,
  Word,
  WordType,
  MoveMode,
  VisibilityState,
  V2,
  Corners,
  GameManager,
  CommandType,
  Command,
  CursorStyle,
  Shape,
  RoomUserEntity,
  ServerObjectId,
  Room,
  AnchoredPosition,
  GameObject,
  ImageUploadResponse,
  Space,
  EntitySyncType,
  type QRCode,
  type UserTextEditor,
  type TextEditor,
  type TextObject2,
  type DropArea,
  type UserText,
  type ImageObject,
  type CmsConsole,
} from './types';
import {
  isZeroV2,
  getShapeDistance,
  normalizeAndMagnitude,
  getZeroCommand,
  addV2,
  clamp,
  distanceBetween,
  divideV2,
  subtractV2,
  v2,
  cloneV2,
  hasVisibleState,
  getRoughShapeDistance,
  makeDenseArray,
  v2AddX,
  scaleV2,
  isEqual,
  cloneTextStyle,
} from './helpers';
import {
  buttonStates,
  clearTypedKeys,
  isSpecialKeyDown,
  resetChangedThisFrameOnButtons,
  typedKeys,
} from './input';
import { CANVAS_TEXT_CAP_MARGIN_RATIO, TEXT_STYLE } from './constants';
import { dt, time, debugState, pgBasePath, thnkBasePath } from './index';
import {
  ObjectType,
  ServerEvent_EntityProps,
  ServerEvent_RoomTerminated,
  ServerEvent_UserCursorData,
  ServerEvent_BackgroundMusic,
  ServerEvent_Settings,
  type ImageDetails,
} from './shared/api_shared_types';
import {
  getEntityState,
  setParent,
  setEntityRoomId,
  moveEntityToBack,
  anchoredPos,
  getAbsolutePos,
  getAbsolutePosAndScale,
  getZeroEntity,
  setEntityPosition,
  getObjectsByType,
  getObjectCountByType,
  getFirstObjectByType,
} from './entity';

import { updateAnimations } from './animation';
import { changeTextObjectText, updateAutoTypeControllers } from './textobject/textobject';
import { checkDetachFromDropAreas, resetDropAreas, checkDropAreaTargets } from './droparea';
import { requestCloseRoom, terminateRoom } from './room';
import {
  createCursorUserText,
  update,
  updateCursorHighlighting,
  updateCursorInteraction,
  updateCursorPosition,
  updateCursorText,
} from './cursor';
import { updateBackground } from './background_controller';
import { synUserTextsToServer } from './user_text';
import { createTextEditor, stopTextEdit, updateTextEditorTransform } from './text_editor';

import { logHierarchy, updateGameObjectHierarchy } from './gameobject';

import logger from './controllers/logger';
import { updateToolTips } from './controllers/tooltip_controller';

export type QRt = any; //QR code js file, not a module, no TS
declare const qrcode: QRt;

export function simulate(manager: GameManager): boolean {
  let cursor = manager.cursor;

  let mousePos = manager.mousePos;

  let commandsToExcecute: Command[] = [];

  let isDirty = false;

  if (debugState.doDebugSkip) return false;

  manager.userEmit.mousePos = manager.mainRoom?.canvas
    ? manager.mainRoom.canvas.layer.relativeMousePos
    : mousePos;

  if (buttonStates.LEFT_MOUSE.changedThisFrame && buttonStates.LEFT_MOUSE.isDown) {
    //cache mouse clicked until its reset on the next emit
    manager.userEmit.mouseClicked = true;
  }

  //handle typed keys
  for (let i = 0; i < typedKeys.length; i++) {
    let k = typedKeys[i];
    if (k.isDebugCommand && manager.user.cmsRole) {
      if (k.code === 49) {
        //1
        debugState.showEntityCounts = !debugState.showEntityCounts;
        isDirty = true;
      } else if (k.code === 50) {
        //2
        debugState.showEntityInfo = !debugState.showEntityInfo;
        isDirty = true;
      } else if (k.code === 51) {
        //3
        debugState.showCmsConsole = !debugState.showCmsConsole;
        const cmsConsole = getFirstObjectByType<CmsConsole>(manager, ObjectType.CMS_CONSOLE);

        if (cmsConsole) {
          if (debugState.showCmsConsole) {
            logger.debug('Show CMS console');
            setVisibilityState(cmsConsole, VisibilityState.DEFAULT);
          } else {
            logger.debug('Hide CMS console');
            setVisibilityState(cmsConsole, VisibilityState.HIDDEN);
          }
        }
      } else if (k.code === 52) {
        //4
        debugState.logDirty = !debugState.logDirty;
        logger.debug('dirty log: ', debugState.logDirty);
      } else if (k.code === 53) {
        //5
        logHierarchy(manager.space);
      } else if (k.code === 54) {
        //6
        debugState.alwaysRedraw = !debugState.alwaysRedraw;
        logger.debug('Always redraw: ', debugState.alwaysRedraw);
      } else if (k.code === 56) {
        //8
        debugState.isDebugMode = !debugState.isDebugMode;
        debugState.isDebugModeChangedThisFrame = true;

        isDirty = true;
      }
    } else {
      if (!manager.cursor.allowTyping) break;
      if (buttonStates.CTRL.isDown || buttonStates.ALT.isDown || buttonStates.META.isDown) {
        if (debugState.isDebugMode) {
          logger.debug(
            {
              ctrl_is_down: buttonStates.CTRL.isDown,
              alt_is_down: buttonStates.ALT.isDown,
              meta_is_down: buttonStates.META.isDown,
            },
            'prevent typing',
          );
        }
        break;
      }
      if (manager.cursor.draggedEntity) break;
      if (buttonStates.LEFT_MOUSE.isDown) break;

      //TODO: do not check this here, but after placing the text, do trim and such
      if (k.code === 32) {
        //space
        if (cursor.text.length === 0) continue; //do not start with space
        if (cursor.text.substring(cursor.text.length - 1) === ' ') continue; //no double spaces
      }

      if (!cursor.textEditor) {
        cursor.text += k.key;
        changeTextObjectText(cursor.userText.textObject, cursor.text);
      }
      cursor.timeSinceLastTextTyped = 0;
      cursor.changed = true;
    }
  }
  clearTypedKeys();

  updateCursorPosition(cursor, dt);
  updateCursorHighlighting(cursor);
  updateCursorInteraction(cursor, dt);

  update(cursor);

  //Update cursor text
  if (cursor.changed) {
    updateCursorText(cursor, time);
  }

  //Create text object from typed text
  if (cursor.text) {
    isDirty = !isEqual(manager.mousePos, manager.lastMousePos);
    const textEditFinished = cursor.textEditor && cursor.textEditor.stopEditingThisFrame;
    const specialKeyDown = isSpecialKeyDown();
    const enterPressed =
      buttonStates.ENTER.changedThisFrame && buttonStates.ENTER.isDown && !specialKeyDown;
    const escapePressed = buttonStates.ESCAPE.changedThisFrame && buttonStates.ESCAPE.isDown;
    const mouseClicked =
      buttonStates.LEFT_MOUSE.changedThisFrame &&
      buttonStates.LEFT_MOUSE.isDown &&
      !manager.cursor.focussedThisMouseDown;

    if (escapePressed) {
      if (cursor.textEditor) {
        stopTextEdit(cursor.textEditor);
        cursor.textEditor = null;
        cursor.text = '';
      }
    } else if (textEditFinished || enterPressed || mouseClicked) {
      if (cursor.textEditor) {
        stopTextEdit(cursor.textEditor);
        cursor.textEditor = null;
      }
      if (cursor.text.length > 0 && cursor.allowCreatingText) {
        const userText = cursor.userText;
        userText.text = userText.textObject.text;
        userText.dragged = false;
        userText.textObject.hideAllText = false;
        userText.textObject.minWidthMod = 1;
        userText.moveMode = MoveMode.STATIC;
        userText.pushBack = true;
        userText.influenceKinetic = false;
        userText.syncType = EntitySyncType.SIMPLE;
        userText.lifeTime = 0;
        userText.scale = 1;
        userText.clickable = true;
        userText.allowHighlight = true;
        userText.allowDrag = true;

        cursor.userText.fixedZIndex = false;
        if (manager.mainRoom.canvas) {
          manager.mainRoom.canvas.attachChild(userText, manager.mainRoom.canvas);
          userText.anchoredPos.relativePos = null;
          userText.anchoredPos.relativePivotPos = null;
          const width = userText.width;
          const pos = v2AddX(manager.mainRoom.canvas.layer.relativeMousePos, -width / 2);
          setEntityPosition(userText, pos.x, pos.y);
        } else if (manager.mainRoom.lobbyLayer.children.length) {
          manager.mainRoom.lobbyLayer.children[0].attachChild(
            userText,
            manager.mainRoom.lobbyLayer.children[0],
          );
          userText.anchoredPos.relativePos = null;
          userText.anchoredPos.relativePivotPos = null;
          const width = userText.width;
        } else {
          manager.mainRoom.attachChild(userText, manager.mainRoom);
          const pos = getAbsolutePos(userText);
          userText.anchoredPos.relativePos = null;
          userText.anchoredPos.relativePivotPos = null;
        }

        userText.textObject.allowHighlight = true;
        userText.textObject.changeDuration = 0.3;
        userText.textObject.textStyle = cloneTextStyle(TEXT_STYLE.USER_TEXT);

        //TODO: we should only set the style here,
        //but then wordstyle is not set properly. Need better logic for this
        changeTextObjectText(userText.textObject, userText.textObject.text);

        userText.droppedThisFrame = true;
        cursor.droppedEntityThisFrame = userText;

        if (!cursor.inputRequestTextObject) {
          const [isCommand, command] = checkTextCommand(userText.textObject.text, userText);

          if (isCommand) {
            userText.autoDisappearTime = 2;
            commandsToExcecute.push(command);

            //when it's a command, prevent the text beging used as input for other systems
          } else {
            userText.requestSyncToServer = true;
          }
        }

        //new cursor object;
        cursor.userText = createCursorUserText(manager);
        setParent(cursor.userText, cursor, v2());
        moveEntityToBack(cursor.userText);
        cursor.userText.fixedZIndex = true;
        cursor.userText.moveMode = MoveMode.ANCHORED;
        cursor.userText.anchoredPos = anchoredPos(v2(0.5, 0.5), v2(22, 0), v2(1, 0.5));

        cursor.style = CursorStyle.simple_white;
        cursor.changed = true;
        cursor.timeSinceLastTextInput = 0;
        cursor.text = '';
      }
    }

    //refocus on cursor text
    //when this click also refocussed on the thnk-world web page, do not place
    //the text, but instead re-focus on the textarea of the cursor
    if (
      cursor.textEditor &&
      buttonStates.LEFT_MOUSE.changedThisFrame &&
      buttonStates.LEFT_MOUSE.isDown &&
      manager.cursor.focussedThisMouseDown
    ) {
      cursor.textEditor.inputElement.focus();
    }
  }

  const isDraggingOtherEntity =
    cursor.draggedEntity !== null && cursor.draggedEntity !== cursor.userText;

  const allowToShowCursorText =
    (cursor.highlightEntities.length === 0 || cursor.text.length > 0) &&
    !isDraggingOtherEntity &&
    getObjectCountByType(manager, ObjectType.USER_TEXT_EDITOR) === 0 &&
    cursor.allowTyping;
  const preferToShowCursorText =
    cursor.inputRequestEntityCount > 0 || cursor.inputRequestTextObject || cursor.text.length > 0;

  let showCursorText = allowToShowCursorText && preferToShowCursorText;

  if (showCursorText) {
    if (!hasVisibleState(cursor.userText.visibilityState)) {
      setVisibilityState(cursor.userText, VisibilityState.APPEARING);
      cursor.startDraggingEntityThisFrame = cursor.userText;
      cursor.draggedEntity = cursor.userText;

      const e = createTextEditor(cursor.userText.textObject);
      e.showBorder = false;

      setParent(e, cursor.userText.textObject);
      updateTextEditorTransform(e);

      manager.cursor.textEditor = e;
      cursor.userText.textObject.hideAllText = true;
    }
  } else {
    if (hasVisibleState(cursor.userText.visibilityState)) {
      setVisibilityState(cursor.userText, VisibilityState.HIDING);
      cursor.startDraggingEntityThisFrame = null;
      //only candel when text wasn't dropped
      cursor.canceledDraggingEntityThisFrame = !cursor.droppedEntityThisFrame;
      cursor.draggedEntity = null;
      if (cursor.textEditor) {
        stopTextEdit(cursor.textEditor);
        cursor.textEditor = null;
      }
    }
  }

  //handle commands
  for (let i = 0; i < commandsToExcecute.length; i++) {
    let command = commandsToExcecute[i];
    executeCommand(command, manager);
  }

  cleanGameObjectLists(manager);

  updateAnimations(manager.animations, dt);

  updateWindow(manager.space);

  updateBackground(manager, dt);

  checkDetachFromDropAreas(
    Array.from(getObjectsByType<DropArea>(manager, ObjectType.DROPAREA)),
    cursor,
  );

  const windowSize = { width: window.innerWidth, height: window.innerHeight };
  updateToolTips();
  const gameObjectDirty = updateGameObjectHierarchy(manager.space, time, false, windowSize);
  updateGameObjectHierarchy(manager.spaceBG, time, false, windowSize);
  isDirty ||= gameObjectDirty;

  manager.objectEventData = {};

  checkDropAreaTargets(
    manager,
    Array.from(getObjectsByType<DropArea>(manager, ObjectType.DROPAREA)),
    cursor,
  );

  updateAutoTypeControllers(manager.autoTypeControllers, manager);

  if (manager.mainRoom?.syncUserText) {
    synUserTextsToServer(Array.from(getObjectsByType<UserText>(manager, ObjectType.USER_TEXT)));
  }

  cursor.droppedEntityThisFrame = null;
  cursor.canceledDraggingEntityThisFrame = false;
  cursor.startDraggingEntityThisFrame = null;
  for (let i = manager.entities.length - 1; i >= 0; i--) {
    const ent = manager.entities[i];
    ent.force.x = 0;
    ent.force.y = 0;
    ent.clickedThisFrame = false;
    ent.droppedThisFrame = false;
    ent.layoutChanged = false;
    ent.highlightLastFrame = ent.highlighted;
    ent.startHighlightThisFrame = false;
    ent.stopHighlightThisFrame = false;
  }

  resetChangedThisFrameOnButtons(buttonStates);

  resetDropAreas(Array.from(getObjectsByType<DropArea>(manager, ObjectType.DROPAREA)));

  debugState.isDebugModeChangedThisFrame = false;

  manager.lastMousePos = cloneV2(mousePos);

  return isDirty;
}

function cleanGameObjectLists(manager: GameManager) {
  //clean up autoTypeControllers
  makeDenseArray(manager.autoTypeControllers, (it) => it.doKill || it.textObject.doKill);

  //clean up drawings
  // makeDenseArray(manager.drawings, (it) => it.doKill);

  //Clean up entities
  makeDenseArray(manager.entities, (it) => it.doKill);

  //destroy questions
  if (manager.cursor.inputRequestTextObject?.doKill) {
    manager.cursor.inputRequestTextObject = null;
  }
}

function updateWindow(playgroundWindow: Space) {}

export function checkTextCommand(text: string, ent: Entity): [boolean, Command] {
  let command = interpretText(text);
  command.entity = ent;
  command.pos = getAbsolutePos(ent);
  if (command.type === CommandType.ACTION) {
    return [true, command];
  } else if (command.type === CommandType.DEBUG) {
    if (ent.manager.user.cmsRole) {
      return [true, command];
    }
  }

  return [false, null];
}

async function htmlImageElementToBlob(img: HTMLImageElement): Promise<Blob> {
  let canvas: HTMLCanvasElement = document.querySelector('#offscreen-canvas');
  const width = img.naturalWidth;
  const height = img.naturalHeight;

  canvas.width = width;
  canvas.height = height;
  canvas.style.display = 'none';

  // canvas.getContext('2d').save();
  // canvas.getContext('2d').scale(-1, 1);
  canvas.getContext('2d').drawImage(img, 0, 0, width, height, 0, 0, width, height);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob));
  });
}

// Image uploading required a server. In the client-only engine there is no
// upload target, so these resolve to their failure values without any network call.
export async function uploadImageBlob(
  _blob: Blob,
  _title: string,
  _alt: string,
  _userToken: string,
): Promise<ImageUploadResponse> {
  return null;
}

export async function uploadProfileImage(_blob: Blob, _userToken: string): Promise<string> {
  return 'error';
}

export function setVisibilityState(entity: Entity, state: VisibilityState) {
  if (!entity) {
    console.error('null passed to setEntityState');
    return;
  }

  if (entity.visibilityState === state) return;

  if (entity.visibilityState === VisibilityState.DISAPPEARING) {
    console.warn('Entity state change while it is disappearing', entity);
  }

  entity.visibilityState = state;
  entity.stateChangedTime = time;
  entity.stateProgress = 0;
  entity.entityStateChanged = true;
  entity.isDirty = true;
}

export function setMultipleEntitiesState(entities: Entity[], state: VisibilityState) {
  for (let i = entities.length - 1; i >= 0; i--) {
    setVisibilityState(entities[i], state);
  }
}

//Not used yet:
function getClosestPointOnCorners(corners: Corners, p: V2): V2 {
  let center = {
    x: corners[0].x + (corners[1].x - corners[0].x) / 2,
    y: corners[3].y + (corners[0].y - corners[3].y) / 2,
  };
  if (p.x >= corners[0].x && p.x <= corners[1].x) {
    if (p.y <= center.y) {
      return { x: p.x, y: corners[0].y };
    } else {
      return { x: p.x, y: corners[2].y };
    }
  } else if (p.y >= corners[0].y && p.y <= corners[3].y) {
    if (p.x <= center.x) {
      return { x: corners[0].x, y: p.y };
    } else {
      return { x: corners[1].x, y: p.y };
    }
  } else {
    if (p.x <= center.x && p.y <= center.y) {
      return { x: corners[0].x, y: corners[0].y };
    } else if (p.x > center.x && p.y <= center.y) {
      return { x: corners[1].x, y: corners[1].y };
    } else if (p.x > center.x && p.y > center.y) {
      return { x: corners[2].x, y: corners[2].y };
    } else if (p.x <= center.x && p.y > center.y) {
      return { x: corners[3].x, y: corners[3].y };
    }
  }

  return { x: 0, y: 0 };
}

function interpretText(text: string): Command {
  let wordArray = text.split(' ').map((it) => it.trim().toLowerCase());
  let textLower = text.toLowerCase();
  let args = wordArray.length > 1 ? wordArray.slice(1) : [];

  let command = getZeroCommand();

  switch (textLower) {
    case 'add_vcss':
      {
        command.type = CommandType.DEBUG;
        command.value = 'add_vcss';
      }
      break;
    case 'reload_seq':
      {
        command.type = CommandType.DEBUG;
        command.value = 'reload_sequence';
      }
      break;
    case 'reconnect_socket':
      {
        command.type = CommandType.DEBUG;
        command.value = 'reconnect_socket';
      }
      break;

    case 'reset_session':
      {
        command.type = CommandType.DEBUG;
        command.value = 'reset_session';
      }
      break;

    case 'jitsi_devices':
      {
        command.type = CommandType.DEBUG;
        command.value = 'jitsi_devices';
      }
      break;

    case 'server_status':
      {
        command.type = CommandType.DEBUG;
        command.value = 'server_status';
      }
      break;
    case 'unlock':
      {
        command.type = CommandType.DEBUG;
        command.value = 'unlock_modules';
      }
      break;
    case 'lock':
      {
        command.type = CommandType.DEBUG;
        command.value = 'lock_modules';
      }
      break;
    case 'server_crash':
      {
        command.type = CommandType.DEBUG;
        command.value = 'server_crash';
      }
      break;
    case 'server_restart':
      {
        command.type = CommandType.DEBUG;
        command.value = 'server_restart';
      }
      break;
    case 'room_status':
      {
        command.type = CommandType.DEBUG;
        command.value = 'room_status';
      }
      break;
    case 'kill_dis_clients':
      {
        command.type = CommandType.DEBUG;
        command.value = 'kill_dis_clients';
      }
      break;
    case 'disconnect_socket':
      {
        command.type = CommandType.DEBUG;
        command.value = 'disconnect_socket';
      }
      break;

    case 'focus':
    case 'fullscreen':
      {
        command.type = CommandType.ACTION;
        command.value = 'fullscreen';
      }
      break;

    case 'device_check':
      {
        command.type = CommandType.ACTION;
        command.value = 'device_check';
      }
      break;

    case 'reset_flags':
      {
        command.type = CommandType.DEBUG;
        command.value = 'reset_flags';
      }
      break;

    default:
  }

  if (command.type === CommandType.NONE) {
    // secondWordLower = wordArray[1].toLowerCase();
    // if (wordLength > 2) {
    // thirdWordLower = wordArray[2].toLowerCase();
    // }

    if (args.length) {
      command.args = args;
      switch (wordArray[0]) {
        case 'serverid':
          {
            command.type = CommandType.DEBUG;
            command.value = 'log_entity_by_serverid';
          }
          break;

        case 'id':
          {
            command.type = CommandType.DEBUG;
            command.value = 'log_entity_by_id';
          }
          break;

        case 'reset_flag':
          {
            command.type = CommandType.DEBUG;
            command.value = 'reset_flag';
          }
          break;

        case 'sequence':
        case 'seq':
          {
            command.type = CommandType.DEBUG;
            command.value = 'start_sequence';
          }
          break;
        case 'join_room':
          {
            command.type = CommandType.DEBUG;
            command.value = 'join_room';
          }
          break;
        case 'user_stats':
          {
            command.type = CommandType.DEBUG;
            command.value = 'get_user_stats';
          }
          break;
        case 'quest_stats':
          {
            command.type = CommandType.DEBUG;
            command.value = 'get_quest_stats';
          }
          break;
        case 'mission_stats':
          {
            command.type = CommandType.DEBUG;
            command.value = 'get_mission_stats';
          }
          break;
        case 'add_vcp':
          {
            command.type = CommandType.ACTION;
            command.value = 'add_vcp';
          }
          break;
        case 'vc_reconnect':
          {
            command.type = CommandType.ACTION;
            command.value = 'vc_reconnect';
          }
          break;
      }
    }
  }

  return command;
}

export function clearCanvas(manager: GameManager) {
  //change to clear all rooms
  manager.settings = {
    offScreenCanvasEnabled: false,
    helpUri: '',
    permissionsHelpUri: '',
    videoCallInfoTitle: '',
    videoCallInfoContent: '',
  };
  clearRooms(manager);
}

function clearRooms(manager: GameManager) {
  const rooms = getObjectsByType<Room>(manager, ObjectType.ROOM);
  for (const room of rooms) {
    if (room === manager.mainRoom) {
      manager.mainRoom = null;
    }

    terminateRoom(room);

    // clearEntities(room.children, immediately);
  }
}

export function getTextFromWords(words: Word[], includeNewLines = false): string {
  let text = '';
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    if (includeNewLines && i > 0 && word.type === WordType.AlineaStart) {
      text += '\n';
    } else {
      text += words[i].text;
    }
  }
  return text;
}

function getNumberFromText(s: string): { isNumber: boolean; value: number } {
  s = s.trim().toLowerCase();
  let num: number = Number.parseInt(s, 10);
  if (!isNaN(num)) {
    return { isNumber: true, value: num };
  } else {
    switch (s) {
      case 'zero':
        return { isNumber: true, value: 0 };
      case 'one':
        return { isNumber: true, value: 1 };
      case 'two':
        return { isNumber: true, value: 2 };
      case 'three':
        return { isNumber: true, value: 3 };
      case 'four':
        return { isNumber: true, value: 4 };
      case 'five':
        return { isNumber: true, value: 5 };
      case 'six':
        return { isNumber: true, value: 6 };
      case 'seven':
        return { isNumber: true, value: 7 };
      case 'eight':
        return { isNumber: true, value: 8 };
      case 'nine':
        return { isNumber: true, value: 9 };
      case 'ten':
        return { isNumber: true, value: 10 };
      default:
        return { isNumber: false, value: 0 };
    }
  }
}

export function getNewEntityId(manager: GameManager): number {
  return ++manager.lastEntityId;
}

export function checkStringSimilarity(stringA: string, stringB: string): number {
  let progress = 0;
  let valueLength = stringA.length;
  if (valueLength === 0) {
    return 0;
  } else {
    let progressPerChar = 1 / (stringB.length - 1);
    let oneWrongChar = false;
    for (let i = 0; i < valueLength; i++) {
      let valueChar = stringA.substring(i, i + 1).toLocaleLowerCase();
      let codeChar = stringB.substring(i, i + 1).toLowerCase();
      if (!oneWrongChar && valueChar === codeChar) {
        progress += progressPerChar;
      } else {
        progress -= progressPerChar;
        oneWrongChar = true;
      }
    }
    progress -= progressPerChar;
    progress = clamp(progress, 0, 1);
  }
  return progress;
}

export function updateDOM(manager: GameManager) {
  let offset = v2(); //manager.perspectiveOffset;

  const cursor = manager.cursor;

  for (const editor of getObjectsByType<UserTextEditor>(manager, ObjectType.USER_TEXT_EDITOR)) {
    const vEl = editor.inputElement;
    if (vEl) {
      const [editorPos, scale] = getAbsolutePosAndScale(editor);
      let pos = addV2(editorPos, editor.userText.textObject.textEnt.pos);
      const offset = 3 * scale - (scale - 1) * 1;

      vEl.style.top = pos.y - offset + 'px';
      vEl.style.left = pos.x + 'px';
      vEl.style.width = Math.max(editor.userText.textObject.textEnt.width + 0, 3) + 'px';
      vEl.style.height = editor.userText.textObject.textEnt.height + 5 + 'px';
      vEl.style.transform = `scale(${scale})`;
    }
  }
  for (const editor of getObjectsByType<TextEditor>(manager, ObjectType.TEXT_EDITOR)) {
    const vEl = editor.inputElement;
    if (vEl) {
      const tObj = editor.tObj;
      const [editorPos, scale] = getAbsolutePosAndScale(editor);
      // const textEntOffset = multiplyV2(tObj.textEnt.pos, scale);

      // let pos = addV2(editorPos, textEntOffset);
      let pos = editorPos;

      vEl.style.transform = `scale(${scale})`;
      const top = pos.y - editor.height / 2;
      const left = pos.x - editor.width / 2;
      vEl.style.top = top + 'px';
      vEl.style.left = left + 'px';

      vEl.style.width = editor.width + 'px';
      vEl.style.height = editor.height + 'px';
    }
  }

  for (const tObj of getObjectsByType<TextObject2>(manager, ObjectType.TEXTOBJECT2)) {
    const el = tObj.editTextArea;
    if (el === null) continue;

    const [pos, scale] = getAbsolutePosAndScale(tObj);
    const innerWidth = tObj.width - tObj.padding[1] - tObj.padding[3];
    const innerHeight = tObj.height - tObj.padding[0] - tObj.padding[2];

    const halfLeading =
      (tObj.defaultTextStyle.fontSize * (tObj.defaultTextStyle.lineHeight - 1)) / 2;

    el.style.left = pos.x - (tObj.width / 2) * scale + tObj.padding[3] * scale + 'px';
    el.style.top =
      pos.y -
      (tObj.height / 2) * scale +
      tObj.padding[0] * scale -
      scale * tObj.defaultTextStyle.fontSize * CANVAS_TEXT_CAP_MARGIN_RATIO -
      scale * halfLeading +
      'px';
    el.style.width = innerWidth + 'px';
    el.style.height = innerHeight + 'px';
    el.style.transform = `scale(${scale})`;
  }
}

function updateOtherUsers(otherUsers: RoomUserEntity[], manager: GameManager) {
  for (let i = 0; i < otherUsers.length; i++) {
    let ou = otherUsers[i];
    let diff = subtractV2(ou.targetPos, ou.pos);
    if (!isZeroV2(diff)) {
      let { normalize, magnitude } = normalizeAndMagnitude(diff);
      const speedGhostUser = 100;
      const speedConnectedUser = 4000;
      const speed = speedConnectedUser;
      const scalar = Math.min(speed * dt, magnitude);
      const move = scaleV2(normalize, scalar);
      const pos = addV2(ou.pos, move);
      setEntityPosition(ou, pos.x, pos.y);
    }

    if (ou.cursorTextObject) {
      if (ou.cursorTextChanged) {
        changeTextObjectText(ou.cursorTextObject, ou.cursorText);
        ou.cursorTextChanged = false;
        if (ou.cursorText && !hasVisibleState(ou.cursorTextObject.visibilityState)) {
          setVisibilityState(ou.cursorTextObject, VisibilityState.APPEARING);
        } else if (!ou.cursorText && hasVisibleState(ou.cursorTextObject.visibilityState)) {
          setVisibilityState(ou.cursorTextObject, VisibilityState.HIDING);
        }
      }

      //offset textobject position to the left of the cursor
      let cursorSideOffset = 8;
      let offsetPos = v2(ou.cursorTextObject.textEnt.width / 2 + cursorSideOffset, -4);
      const pos = subtractV2(ou.pos, offsetPos);
      setEntityPosition(ou.cursorTextObject, pos.x, pos.y);
    }
  }
}

export function syncOtherClients(manager: GameManager, data: ServerEvent_UserCursorData[]) {
  let otherUsers = Array.from(getObjectsByType<RoomUserEntity>(manager, ObjectType.OTHER_CLIENT));
  let mySlug = manager.user.slug;

  const length = data.length;
  for (let i = 0; i < length; i++) {
    if (data[i].slug === mySlug) continue; //is local user
    const index = otherUsers.findIndex((u) => u.userSlug === data[i].slug);

    const newLastCursorData = data[i].lastCursorData;

    if (index >= 0) {
      //update user
      const otherUser = otherUsers[index];
      if (newLastCursorData) {
        if (newLastCursorData.mousePosX && newLastCursorData.mousePosY) {
          otherUser.targetPos = v2(newLastCursorData.mousePosX, newLastCursorData.mousePosY);
        } else {
          otherUser.targetPos = v2();
        }
        if (newLastCursorData.mouseClicked) {
          otherUser.lastClickedTime = time;
        }
        if (newLastCursorData.text !== otherUser.cursorText) {
          otherUser.cursorText = newLastCursorData.text;
          otherUser.cursorTextChanged = true;
        }
      }
    }
  }
}

export function mouseDownThisFrame(): boolean {
  return buttonStates.LEFT_MOUSE.changedThisFrame && buttonStates.LEFT_MOUSE.isDown;
}

export function mouseUpThisFrame(): boolean {
  return buttonStates.LEFT_MOUSE.changedThisFrame && !buttonStates.LEFT_MOUSE.isDown;
}

export function isMouseDown(): boolean {
  return buttonStates.LEFT_MOUSE.isDown;
}

export async function syncSettings(manager: GameManager, data: ServerEvent_Settings) {
  if ('helpUri' in data) {
    manager.settings.helpUri = data.helpUri;
  }

  if ('permissionsHelpUri' in data) {
    manager.settings.permissionsHelpUri = data.permissionsHelpUri;
  }

  if ('videoCallInfoTitle' in data) {
    manager.settings.videoCallInfoTitle = data.videoCallInfoTitle;
  }

  if ('videoCallInfoContent' in data) {
    manager.settings.videoCallInfoContent = data.videoCallInfoContent;
  }
}

export function setEntitiesDisappearByServerId(manager: GameManager, serverIds: ServerObjectId[]) {
  const matchingEntities = filterEntitiesByServerIds(manager.entities, serverIds);

  for (let i = 0; i < matchingEntities.length; i++) {
    const ent = matchingEntities[i];
    setVisibilityState(ent, VisibilityState.DISAPPEARING);
  }
}

export function cleanLocalUserTexts(manager: GameManager) {
  for (const userText of getObjectsByType<UserText>(manager, ObjectType.USER_TEXT)) {
    if (userText.parent.objectType === ObjectType.ROOM) {
      setVisibilityState(userText, VisibilityState.DISAPPEARING);
    }
  }
}

function filterEntitiesByServerIds(entities: Entity[], serverIds: number[]): Entity[] {
  let matchingEntities = entities.filter((e) => isEntityIdInList(e.serverObjId, serverIds));

  return matchingEntities;
}

function isEntityIdInList(entityId: number, idList: number[]): boolean {
  return idList.findIndex((id) => id === entityId) >= 0;
}
export function setParentByEntityProps(
  manager: GameManager,
  gameObject: GameObject,
  serverId: ServerObjectId,
  roomId: RoomId,
  entityProps: ServerEvent_EntityProps | null,
): [Room, GameObject] {
  // gameObject.serverObjId = serverId;

  let maybeParent = gameObject.parent;

  if (!maybeParent && entityProps && 'parSId' in entityProps) {
    maybeParent = manager.gameObjectServerIdTable[entityProps.parSId];

    if (maybeParent) {
      maybeParent.attachChild(gameObject, maybeParent);
    } else {
      console.error(
        `Cannot parent entity because entity with serverId ${entityProps.parSId} cannot be found`,
      );
    }
  }

  //NOTE: altough a parent was found, parenting could have
  //failed (because of a hierarchy-loop).

  // setEntityRoomId( entity, props.roomId );
  let room: Room = null;
  for (const r of getObjectsByType<Room>(gameObject.manager, ObjectType.ROOM)) {
    if (r.roomId === roomId) {
      room = r;
      break;
    }
  }
  if (room) {
    if (!gameObject.parent) {
      room.attachChild(gameObject, room);
    } else {
      setEntityRoomId(gameObject, room.roomId);
    }
  } else {
    console.error('Cannot parent entity to room', serverId, roomId);
  }

  return [room, gameObject.parent];
}

export function terminateRoomById(manager: GameManager, data: ServerEvent_RoomTerminated) {
  let room: Room = null;
  for (const r of getObjectsByType<Room>(manager, ObjectType.ROOM)) {
    if (r.roomId === data.roomId) {
      room = r;
      break;
    }
  }

  if (!room) {
    console.error('Cannot termindate room, room with id not found.', { data });
    return;
  }

  terminateRoom(room);

  //TODO: show msg if send by server
}

export function setEntityServerId(manager: GameManager, json: any) {
  if (!json.hasOwnProperty('clientId')) return;
  if (!json.hasOwnProperty('serverId')) return;

  let ent = manager.gameObjectIdTable[json.clientId];
  if (!ent) return;
  ent.serverObjId = json.sId;
}

export function setPositionByEntityProps(ent: Entity, entityProps: ServerEvent_EntityProps) {
  let anchoredPosTemp: AnchoredPosition = anchoredPos(null, v2(), null);
  let doSetPosition = false;

  if (entityProps.hasOwnProperty('relX') || entityProps.hasOwnProperty('relY')) {
    const relX = 'relX' in entityProps ? entityProps.relX : 0.5;
    const relY = 'relY' in entityProps ? entityProps.relY : 0.5;
    if (anchoredPosTemp.relativePos) {
      anchoredPosTemp.relativePos.x = relX;
      anchoredPosTemp.relativePos.y = relY;
    } else {
      anchoredPosTemp.relativePos = v2(relX, relY);
    }
    if (!anchoredPosTemp.relativePivotPos) {
      anchoredPosTemp.relativePivotPos = v2(0.5, 0.5);
    }
    doSetPosition = true;
  }

  if (entityProps.hasOwnProperty('pivX') || entityProps.hasOwnProperty('pivY')) {
    const pivX = 'pivX' in entityProps ? entityProps.pivX : 0.5;
    const pivY = 'pivY' in entityProps ? entityProps.pivY : 0.5;
    if (anchoredPosTemp.relativePivotPos) {
      anchoredPosTemp.relativePivotPos.x = pivX;
      anchoredPosTemp.relativePivotPos.y = pivY;
    } else {
      anchoredPosTemp.relativePivotPos = v2(pivX, pivY);
    }

    doSetPosition = true;
  } else if (ent.anchoredPos && ent.anchoredPos.relativePivotPos) {
    anchoredPosTemp.relativePivotPos = { ...ent.anchoredPos.relativePivotPos };
  }

  if (entityProps.hasOwnProperty('X')) {
    anchoredPosTemp.offset.x = entityProps.X;
    doSetPosition = true;
  }
  if (entityProps.hasOwnProperty('Y')) {
    anchoredPosTemp.offset.y = entityProps.Y;
    doSetPosition = true;
  }

  if ('moveMode' in entityProps) {
    doSetPosition = true;
    switch (entityProps.moveMode) {
      case 'anchored':
        {
          ent.moveMode = MoveMode.ANCHORED;
          ent.allowDrag = false;
        }
        break;
      case 'static':
        {
          ent.moveMode = MoveMode.STATIC;
          ent.allowDrag = false;
        }
        break;
      case 'kinetic':
        {
          ent.moveMode = MoveMode.KINETIC;

          ent.allowHighlight = true;
          ent.allowDrag = true;
          ent.passClickCanvas = false;
        }
        break;

      default: {
        console.error(
          `unknown movemode in entitytProps of serverId ${ent.serverObjId}: ${entityProps.moveMode}`,
        );
      }
    }
  }

  if (doSetPosition) {
    if (ent.moveMode === MoveMode.ANCHORED) {
      //set the anchored position
      if (!anchoredPosTemp.relativePos) {
        anchoredPosTemp.relativePos = v2(0.5, 0.5);
        anchoredPosTemp.relativePivotPos = v2(0.5, 0.5);
      }
    }

    ent.anchoredPos = anchoredPosTemp;
  }
}

export function resetSession(manager: GameManager, serverSession: string) {
  clearRooms(manager);

  cleanGameObjectLists(manager);

  manager.serverSession = serverSession;

  manager.requestCloseOverlayEntities = true;

  manager.cursor.allowTyping = true;
}

export async function syncBackgroundMusic(manager: GameManager, data: ServerEvent_BackgroundMusic) {
  const uri = data.trackUri;
  const volume = data.volume;
  const loop = data.loop;

  if ('trackUri' in data && !data.trackUri.length) {
    stopBackgroundMusic(manager);
  } else {
    setBackgroundMusic(manager, data.trackUri, volume, loop);
  }
}

export function setBackgroundMusic(
  manager: GameManager,
  trackUri: string,
  volume: number,
  loop: boolean,
) {
  if (!manager.backgroundMusic) {
    manager.backgroundMusic = {
      currentUrl: '',
      requestPlayTrack: trackUri,
      requestStopTrack: false,
      volume,
      loop,
    };
  } else {
    if (manager.backgroundMusic.requestPlayTrack !== trackUri) {
      manager.backgroundMusic.loop = loop;
      manager.backgroundMusic.volume = volume;
      manager.backgroundMusic.requestPlayTrack = trackUri;
    }
  }
}

export function stopBackgroundMusic(manager: GameManager) {
  if (manager.backgroundMusic) {
    if (manager.backgroundMusic.requestPlayTrack) {
      manager.backgroundMusic.requestPlayTrack = '';
    }
    manager.backgroundMusic.requestStopTrack = true;
  }
}

export function disconnectedFromServer(manager: GameManager) {
  manager.isConnectedToServer = false;

  console.warn(`socket disconnected`);

  if (manager.clientReady) {
    manager.clientReady = false;
  }
}

export function executeCommand(action: Command, manager: GameManager) {
  switch (action.value) {
    case 'log_entity_by_serverid':
      {
        if (action.args.length && typeof action.args[0] === 'string') {
          let serverId = parseInt(action.args[0]);
          if (!isNaN(serverId)) {
            let entity = manager.entities.filter((it) => it.serverObjId === serverId);
            logger.debug(entity);
          }
        }
      }
      break;
    case 'nav_back':
      {
        requestCloseRoom(manager.mainRoom);
      }
      break;

    case 'log_entity_by_id':
      {
        if (action.args.length && typeof action.args[0] === 'string') {
          let id = parseInt(action.args[0]);
          if (!isNaN(id)) {
            if (id === 0) {
              logger.debug(manager);
            } else {
              let entity = manager.entities.filter((it) => it.id === id);
              logger.debug(entity);
            }
          }
        }
      }
      break;
    case 'fullscreen':
      {
        // make the element go to full-screen mode
        toggleFullscreen();
      }
      break;
    case 'upload_image':
      {
        const imObj = getObjectsByType<ImageObject>(manager, ObjectType.IMAGE_OBJECT)
          .values()
          .next().value;
        if (imObj) {
          imObj.requestImageUpload = true;
        }
      }
      break;

    case 'close_room':
      {
        if (action.args.length === 1 && typeof action.args[0] === 'string') {
          let roomId = action.args[0];
          requestCloseRoom(manager.mainRoom);
        }
      }
      break;
    case 'external_link': // same tab
      {
        let link = null;
        try {
          if (action.args.length) {
            link = new URL(action.args[0]);
          }
        } catch {
          console.warn('Link is not valid', { link: action.args });
        }
        if (link) {
          open(link, '_blank');
        }
      }
      break;

    case 'local_link': // same tab
      {
        let link = null;
        try {
          if (action.args.length) {
            link = new URL(action.args[0], thnkBasePath);
          }
        } catch {
          console.warn('Link is not valid', { link: action.args });
        }
        if (link) {
          open(link, '_self');
        }
      }
      break;
    case 'local_link_blank':
      {
        let link = null;
        try {
          if (action.args.length) {
            link = new URL(action.args[0], thnkBasePath);
          }
        } catch {
          console.warn('Link is not valid', { link: action.args });
        }
        if (link) {
          open(link, '_blanc');
        }
      }
      break;
    case 'local_link_pg_blank':
      {
        let link = null;
        try {
          if (action.args.length) {
            link = new URL(action.args[0], pgBasePath);
          }
        } catch {
          console.warn('Link is not valid', { link: action.args });
        }
        if (link) {
          open(link, '_blank');
        }
      }
      break;
    case 'local_link_pg':
      {
        let link = null;
        try {
          if (action.args.length) {
            link = new URL(action.args[0], pgBasePath);
          }
        } catch {
          console.warn('Link is not valid', { link: action.args });
        }
        if (link) {
          open(link, '_self');
        }
      }
      break;

    default:
      if (debugState.isDebugMode) {
        logger.debug('Unknown button command', action);
      }
      break;
  }
}

export function toggleFullscreen() {
  let element = document.querySelector('body');

  const isFullscreen = document.fullscreenElement === element;

  if (isFullscreen) {
    document
      .exitFullscreen()
      .then(() => {
        // onFullScreenEnd(overlay);
      })
      .catch(function (error) {
        // element could not enter fullscreen mode
      });
  } else {
    element
      .requestFullscreen()
      .then(() => {
        // onFullScreenStart(overlay);
      })
      .catch(function (error) {
        // element could not enter fullscreen mode
      });
  }
}

export function entityPushback(entities: Entity[]) {
  const springConstantRepulse = 30;

  for (let i = 0; i < entities.length; i++) {
    let entA = entities[i];
    const entA_State = getEntityState(entA);
    if (entA_State === VisibilityState.HIDDEN) continue;

    if (entA.moveMode !== MoveMode.KINETIC && !entA.influenceKinetic) continue;

    for (let j = i + 1; j < entities.length; j++) {
      let entB = entities[j];

      if (entA.parent !== entB.parent) continue;

      const entB_State = getEntityState(entB);
      if (entB_State === VisibilityState.HIDDEN) continue;

      //This check does not add performance, it makes performance worse!
      // if (!entitiesKineticInfluence(entA, entB)) continue;

      let posA = entA.pos;
      let posB = entB.pos;
      let dist = distanceBetween(posA, posB);

      {
        let roughSpringLength = getRoughShapeDistance(entA, entB) + 10;

        let d = dist - roughSpringLength;
        if (d >= 0) continue;
      }

      let springLength = getShapeDistance(entA, entB) + 10;
      let dPos = subtractV2(posA, posB);

      let unit = dist !== 0 ? divideV2(dPos, dist) : entA.width > entA.height ? v2(0, 1) : v2(1, 0);
      let d = dist - springLength;
      if (d >= 0) continue;

      let springForce = v2();
      springForce.x = -1 * springConstantRepulse * d * unit.x;
      springForce.y = -1 * springConstantRepulse * d * unit.y;

      if (entA.moveMode === MoveMode.KINETIC && entB.influenceKinetic) {
        entA.force = addV2(entA.force, springForce);
      }
      if (entB.moveMode === MoveMode.KINETIC && entA.influenceKinetic) {
        entB.force = subtractV2(entB.force, springForce);
      }
    }
  }
}

export function applyEntityForce(entity: Entity, currentPos: V2, sub_dt: number): [number, number] {
  let manager = entity.manager;

  if (entity.visibilityState === VisibilityState.NONE) return [currentPos.x, currentPos.y];

  // if ( ent.parent ) continue;
  let vel = v2();
  let pos = v2();

  let acc = divideV2(entity.force, 20);

  let cappedDt = clamp(sub_dt, 0, 0.5);

  //velocity
  // const dtSquare = cappedDt * cappedDt;
  vel = acc;

  //position

  if (Math.abs(vel.x) < 0.01) {
    vel.x = 0;
  }
  if (Math.abs(vel.y) < 0.01) {
    vel.y = 0;
  }

  pos.x = currentPos.x + sub_dt * vel.x;
  pos.y = currentPos.y + sub_dt * vel.y;

  //insert final position checks here

  entity.vel = vel;
  if (isNaN(pos.x) || isNaN(pos.y)) {
    console.error('Position is NAN', entity);
    entity.vel = v2();
    pos = cloneV2(currentPos);
  }

  return [pos.x, pos.y];
}

export function createQRCode(manager: GameManager, text: string): QRCode {
  let newQRCode: QRCode = {
    ...getZeroEntity(),
    objectType: ObjectType.QRCODE,
    text,
    image: null,
    ready: false,
  };

  newQRCode.shape = Shape.RECT;

  let qr = qrcode(0, 'L');
  qr.addData(text);
  qr.make();

  let imBase64 = qr.createDataURL(5, 20);

  newQRCode.image = new Image();

  newQRCode.image.src = imBase64;

  newQRCode.image.onload = function () {
    newQRCode.ready = true;
  };

  // addQRCode(manager, newQRCode);

  setVisibilityState(newQRCode, VisibilityState.APPEARING);

  return newQRCode;
}
